/* JPI universal storefront layer — additive to Shopify Ride. Version 0.3.3 */
(function () {
  'use strict';

  if (window.JpiUniversalLayer) return;
  window.JpiUniversalLayer = { version: '0.3.3' };

  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'summary',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function setDisclosureState(details) {
    const summary = details.querySelector(':scope > summary');
    if (summary) summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
  }

  function getVisibleFocusable(container) {
    return Array.from(container.querySelectorAll(focusableSelector)).filter((element) => {
      return !element.hasAttribute('hidden') && element.getClientRects().length > 0;
    });
  }

  function syncOverlayState() {
    const overlayIsOpen = Boolean(document.querySelector('details[data-jpi-focus-trap][open]'));
    document.body.classList.toggle('jpi-overlay-open', overlayIsOpen);
  }

  class JpiUniversalHeader extends HTMLElement {
    connectedCallback() {
      if (this.dataset.jpiReady === 'true') return;
      this.dataset.jpiReady = 'true';
      this.abortController = new AbortController();
      const signal = this.abortController.signal;
      /* TIMESTAMP START 2026-08-21T16:31:58-04:00 | REVISION R000187 */
      if (!document.getElementById('cart-icon-bubble')) {
        const compatibilityBubble = document.createElement('span');
        compatibilityBubble.id = 'cart-icon-bubble';
        compatibilityBubble.hidden = true;
        compatibilityBubble.setAttribute('aria-hidden', 'true');
        this.appendChild(compatibilityBubble);
      }

      const desktopCartLink = this.querySelector('.jpi-header__icon-button[href*="/cart"]');
      const mobileCartLink = this.querySelector('.jpi-header__mobile-utilities a[href*="/cart"]');
      const mobileCartLabel = mobileCartLink && mobileCartLink.querySelector(':scope > span:last-child');
      const serverCartCount = desktopCartLink && desktopCartLink.querySelector(':scope > .jpi-header__cart-count');
      const baseCartLabel = this.cartBaseLabel || (
        (desktopCartLink && desktopCartLink.getAttribute('aria-label')) ||
        (mobileCartLabel && mobileCartLabel.textContent.replace(/\s*\(\d+\)\s*$/, '')) ||
        ''
      ).trim();
      this.cartBaseLabel = baseCartLabel;
      const cartItemsLabel = (this.dataset.jpiCartItemsLabel || '').trim();
      let cartCountRequestSequence = 0;

      const syncCartCount = (value) => {
        const parsedCount = Number(value);
        if (!Number.isFinite(parsedCount)) return;

        const count = Math.max(0, Math.trunc(parsedCount));
        const countLabel = [String(count), cartItemsLabel].filter(Boolean).join(' ');
        const accessibleLabel = count > 0
          ? [baseCartLabel, countLabel].filter(Boolean).join(', ')
          : baseCartLabel;

        if (desktopCartLink) {
          let countBadge = desktopCartLink.querySelector(':scope > .jpi-header__cart-count');
          let countStatus = desktopCartLink.querySelector(
            ':scope > .jpi-header__cart-count + .jpi-visually-hidden'
          );

          if (count > 0) {
            if (!countBadge) {
              countBadge = document.createElement('span');
              countBadge.className = 'jpi-header__cart-count';
              countBadge.setAttribute('aria-hidden', 'true');
              desktopCartLink.appendChild(countBadge);
            }
            countBadge.textContent = String(count);

            if (!countStatus) {
              countStatus = document.createElement('span');
              countStatus.className = 'jpi-visually-hidden';
              desktopCartLink.appendChild(countStatus);
            }
            countStatus.textContent = countLabel;
          } else {
            if (countBadge) countBadge.remove();
            if (countStatus) countStatus.remove();
          }

          if (accessibleLabel) desktopCartLink.setAttribute('aria-label', accessibleLabel);
        }

        if (mobileCartLink) {
          if (mobileCartLabel && baseCartLabel) {
            mobileCartLabel.textContent = count > 0 ? `${baseCartLabel} (${count})` : baseCartLabel;
          }
          if (accessibleLabel) mobileCartLink.setAttribute('aria-label', accessibleLabel);
        }
      };

      const refreshCartCount = async () => {
        const requestSequence = ++cartCountRequestSequence;
        const cartRoute = typeof routes !== 'undefined' && routes.cart_url ? routes.cart_url : '/cart';
        const cartUrl = cartRoute.endsWith('.js') ? cartRoute : `${cartRoute}.js`;

        try {
          const response = await fetch(cartUrl, {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
            signal
          });
          if (!response.ok) return;

          const cartData = await response.json();
          if (requestSequence === cartCountRequestSequence) syncCartCount(cartData.item_count);
        } catch (error) {
          if (error.name !== 'AbortError') return;
        }
      };

      const mobileServerCount = mobileCartLabel && mobileCartLabel.textContent.match(/\((\d+)\)\s*$/);
      const initialCartCount = serverCartCount
        ? serverCartCount.textContent.trim()
        : mobileServerCount
          ? mobileServerCount[1]
          : 0;
      syncCartCount(initialCartCount);

      if (
        typeof subscribe === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined' &&
        PUB_SUB_EVENTS.cartUpdate
      ) {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          const eventCount = event && event.cartData && event.cartData.item_count;
          if (eventCount !== undefined && eventCount !== null && Number.isFinite(Number(eventCount))) {
            cartCountRequestSequence += 1;
            syncCartCount(eventCount);
            return;
          }
          void refreshCartCount();
        });
      }
      /* TIMESTAMP END 2026-08-21T16:31:58-04:00 | REVISION R000187 */
      const updateOverlayMeasurements = () => {
        const viewportHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight;
        const availableHeight = Math.max(160, viewportHeight - this.getBoundingClientRect().bottom);
        this.style.setProperty('--jpi-drawer-max-height', `${Math.round(availableHeight)}px`);
      };

      this.querySelectorAll('details[data-jpi-disclosure]').forEach((details) => {
        setDisclosureState(details);
        details.addEventListener('toggle', () => {
          setDisclosureState(details);
          syncOverlayState();
          if (!details.open) return;
          if (details.classList.contains('jpi-header__mobile-menu')) updateOverlayMeasurements();

          this.querySelectorAll('details[data-jpi-disclosure][open]').forEach((other) => {
            if (other !== details && !other.contains(details) && !details.contains(other)) {
              other.removeAttribute('open');
              setDisclosureState(other);
            }
          });

          const focusTarget = details.querySelector('[data-jpi-autofocus]');
          if (focusTarget) window.requestAnimationFrame(() => focusTarget.focus());
        }, { signal });
      });

      this.addEventListener('click', (event) => {
        /* Generated backdrop clicks target the open <details> itself. */
        const menuBackdrop = event.target.closest('.jpi-header__mobile-menu[open]');
        if (menuBackdrop && event.target === menuBackdrop) {
          const summary = menuBackdrop.querySelector(':scope > summary');
          menuBackdrop.removeAttribute('open');
          setDisclosureState(menuBackdrop);
          if (summary) summary.focus();
          return;
        }

        const searchBackdrop = event.target.closest('.jpi-header__search-panel');
        if (searchBackdrop && event.target === searchBackdrop) {
          const details = searchBackdrop.closest('details');
          const summary = details && details.querySelector(':scope > summary');
          if (details) details.removeAttribute('open');
          if (details) setDisclosureState(details);
          if (summary) summary.focus();
          return;
        }

        const closeButton = event.target.closest('[data-jpi-close-details]');
        if (!closeButton) return;
        const details = closeButton.closest('details');
        if (!details) return;
        const summary = details.querySelector(':scope > summary');
        details.removeAttribute('open');
        setDisclosureState(details);
        if (summary) summary.focus();
      }, { signal });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'Tab') {
          const trappedDetails = event.target.closest('details[data-jpi-focus-trap][open]');
          const focusScope = trappedDetails && trappedDetails.querySelector(':scope > [data-jpi-focus-scope]');
          if (focusScope) {
            const focusable = getVisibleFocusable(focusScope);
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (first && last && event.shiftKey && document.activeElement === first) {
              event.preventDefault();
              last.focus();
            } else if (first && last && !event.shiftKey && document.activeElement === last) {
              event.preventDefault();
              first.focus();
            }
          }
        }

        if (event.key !== 'Escape') return;
        const details = event.target.closest('details[open]');
        if (!details) return;
        const summary = details.querySelector(':scope > summary');
        details.removeAttribute('open');
        setDisclosureState(details);
        if (summary) summary.focus();
      }, { signal });

      document.addEventListener('click', (event) => {
        if (this.contains(event.target)) return;
        this.querySelectorAll('details[data-jpi-disclosure][open]').forEach((details) => {
          details.removeAttribute('open');
          setDisclosureState(details);
        });
      }, { signal });

      window.addEventListener('resize', updateOverlayMeasurements, { signal });
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', updateOverlayMeasurements, { signal });
      }

      updateOverlayMeasurements();
      syncOverlayState();
    }

    disconnectedCallback() {
      /* TIMESTAMP START 2026-08-21T16:31:58-04:00 | REVISION R000187 */
      if (this.cartUpdateUnsubscriber) {
        this.cartUpdateUnsubscriber();
        this.cartUpdateUnsubscriber = null;
      }
      /* TIMESTAMP END 2026-08-21T16:31:58-04:00 | REVISION R000187 */
      if (this.abortController) this.abortController.abort();
      this.style.removeProperty('--jpi-drawer-max-height');
      delete this.dataset.jpiReady;
      window.requestAnimationFrame(syncOverlayState);
    }
  }

  class JpiPredictiveSearch extends HTMLElement {
    connectedCallback() {
      if (this.dataset.jpiReady === 'true') return;
      this.dataset.jpiReady = 'true';
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-jpi-search-results]');
      this.status = this.querySelector('[data-jpi-search-status]');
      this.form = this.querySelector('form[role="search"]');
      this.timer = null;
      this.requestController = null;
      this.abortController = new AbortController();

      if (!this.input || !this.results || !this.dataset.predictiveUrl) return;

      this.input.addEventListener('input', () => {
        window.clearTimeout(this.timer);
        const term = this.input.value.trim();
        if (term.length < 2) {
          this.clearResults();
          return;
        }
        this.timer = window.setTimeout(() => this.fetchResults(term), 220);
      }, { signal: this.abortController.signal });

      this.input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.clearResults();
      }, { signal: this.abortController.signal });
    }

    async fetchResults(term) {
      if (this.requestController) this.requestController.abort();
      this.requestController = new AbortController();
      this.setStatus(this.dataset.loadingLabel || 'Loading');

      try {
        const url = new URL(this.dataset.predictiveUrl, window.location.origin);
        url.searchParams.set('q', term);
        url.searchParams.set('resources[type]', 'product,collection,page,article');
        url.searchParams.set('resources[limit]', '6');
        url.searchParams.set('resources[options][unavailable_products]', 'last');
        url.searchParams.set('section_id', this.dataset.sectionId || 'jpi-predictive-search');

        const response = await fetch(url.toString(), {
          headers: { Accept: 'text/html' },
          signal: this.requestController.signal
        });
        if (!response.ok) throw new Error(`Predictive search returned ${response.status}`);

        const html = await response.text();
        if (this.input.value.trim() !== term) return;
        this.results.innerHTML = html;
        this.results.hidden = false;
        this.setStatus(this.dataset.resultsLabel || 'Suggestions updated');
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.clearResults();
        this.setStatus(this.dataset.errorLabel || 'Suggestions are unavailable. Submit the form to search.');
      }
    }

    clearResults() {
      if (this.requestController) this.requestController.abort();
      if (this.results) {
        this.results.hidden = true;
        this.results.innerHTML = '';
      }
      this.setStatus('');
    }

    setStatus(message) {
      if (this.status) this.status.textContent = message;
    }

    disconnectedCallback() {
      window.clearTimeout(this.timer);
      if (this.requestController) this.requestController.abort();
      if (this.abortController) this.abortController.abort();
      delete this.dataset.jpiReady;
    }
  }

  if (!customElements.get('jpi-universal-header')) {
    customElements.define('jpi-universal-header', JpiUniversalHeader);
  }
  if (!customElements.get('jpi-predictive-search')) {
    customElements.define('jpi-predictive-search', JpiPredictiveSearch);
  }

  function initializeFooterImpactCounters(root) {
    const scope = root || document;
    const counters = scope.querySelectorAll('#jpi-site-footer .jpi-footer__stats strong');

    counters.forEach((element) => {
      if (element.dataset.jpiCountReady === 'true') return;

      const original = element.textContent.trim();
      const match = original.match(/^([\d,]+)(.*)$/);
      if (!match) return;

      const target = Number(match[1].replace(/,/g, ''));
      const suffix = match[2] || '';
      if (!Number.isFinite(target) || target <= 0) return;

      element.dataset.jpiCountReady = 'true';
      element.setAttribute('aria-label', original);

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      const format = (value) => `${new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        maximumFractionDigits: 0
      }).format(value)}${suffix}`;
      const start = Math.max(0, target - 13);
      const slowStart = Math.max(start, target - 10);

      const begin = () => {
        let current = start;
        element.textContent = format(current);

        const fast = window.setInterval(() => {
          current += 1;
          element.textContent = format(Math.min(current, slowStart));

          if (current >= slowStart) {
            window.clearInterval(fast);
            const slow = window.setInterval(() => {
              current += 1;
              element.textContent = format(Math.min(current, target));
              if (current >= target) window.clearInterval(slow);
            }, 140);
          }
        }, 55);
      };

      if (!('IntersectionObserver' in window)) {
        begin();
        return;
      }

      const observer = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting) return;
        begin();
        observer.disconnect();
      }, { threshold: 0.35 });

      observer.observe(element);
    });
  }

  initializeFooterImpactCounters(document);

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('jpi-universal-header, jpi-predictive-search').forEach((element) => {
      if (typeof element.connectedCallback === 'function') element.connectedCallback();
    });
    initializeFooterImpactCounters(event.target);
  });

  document.addEventListener('shopify:block:select', (event) => {
    const selected = event.target.closest('[data-shopify-editor-block]');
    if (!selected) return;
    const details = selected.closest('details');
    if (details) details.setAttribute('open', '');
  });

  window.JpiUniversalLayer.focusableSelector = focusableSelector;
  window.JpiUniversalLayer.initializeFooterImpactCounters = initializeFooterImpactCounters;
})();


/* TIMESTAMP START 2026-08-19T15:45:40-04:00 | REVISION R000080 */
(() => {
  "use strict";

  const youtubeEmbedSelector =
    'iframe[src*="youtube.com/embed/"], iframe[src*="youtube-nocookie.com/embed/"]';
  const browserLocale =
    ((navigator.languages && navigator.languages[0]) || navigator.language || "en").trim();
  const captionLanguage = browserLocale.split("-")[0].toLowerCase();

  function applyBrowserCaptionLanguage(iframe) {
    if (!(iframe instanceof HTMLIFrameElement)) return;

    const rawSource = iframe.getAttribute("src");
    if (!rawSource) return;

    let source;
    try {
      source = new URL(rawSource, window.location.href);
    } catch (_error) {
      return;
    }

    const isYouTubeEmbed =
      /^(www\.)?youtube(-nocookie)?\.com$/.test(source.hostname) &&
      source.pathname.startsWith("/embed/");
    if (!isYouTubeEmbed) return;

    if (
      source.searchParams.get("hl") === browserLocale &&
      source.searchParams.get("cc_lang_pref") === captionLanguage &&
      source.searchParams.get("cc_load_policy") === "1"
    ) {
      return;
    }

    source.searchParams.set("hl", browserLocale);
    source.searchParams.set("cc_lang_pref", captionLanguage);
    source.searchParams.set("cc_load_policy", "1");
    iframe.src = source.toString();
  }

  function localizeYouTubeEmbeds(root) {
    if (root instanceof Element && root.matches(youtubeEmbedSelector)) {
      applyBrowserCaptionLanguage(root);
    }

    if (root.querySelectorAll) {
      root.querySelectorAll(youtubeEmbedSelector).forEach(applyBrowserCaptionLanguage);
    }
  }

  localizeYouTubeEmbeds(document);

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === "attributes") {
        applyBrowserCaptionLanguage(mutation.target);
        return;
      }

      mutation.addedNodes.forEach(localizeYouTubeEmbeds);
    });
  }).observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["src"],
    childList: true,
    subtree: true,
  });
})();
/* TIMESTAMP END 2026-08-19T15:45:40-04:00 | REVISION R000080 */
